import { Select, SelectProps } from 'zens-storybook'
import React, { useEffect, useState } from 'react'
import { StyleSheet } from 'react-native'
import { useFloors, useInfiniteWorkspaces, useWorkspace, Workspace } from 'zens-store'
import { useTranslation } from 'react-i18next'

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
})

interface Props extends Omit<SelectProps<number>, 'items' | 'onSelect'> {
  companyId: number
  buildingId?: number
  floorId?: number
  onChange?: (value: number) => void
}
const WorkspaceSelect = (props: Props) => {
  const { companyId, buildingId, floorId, onChange, value, ...otherProps } = props
  const [search, setSearch] = useState('')
  const { t } = useTranslation()
  const workspace = useWorkspace({ companyId, workspaceId: value as number })
  const workspaces = useInfiniteWorkspaces(
    {
      companyId,
      filter: {
        ...(floorId ? { floorId: [floorId] } : {}),
        ...(search ? { search } : {}),
      },
      sort: { field: 'name', direction: 'asc' },
      pagination: { page: 0, size: 20 },
    },
    {
      enabled: !value || !!workspace.data?.name,
    },
  )
  const floorIds = useFloors(
    {
      companyId,
      buildingId,
    },
    { enabled: !!buildingId },
  ).data?.map((floor) => floor.id)

  useEffect(() => {
    if (workspace.data && !search) {
      setSearch(workspace.data.name || '')
    }
  }, [workspace.data, search])

  useEffect(() => {
    setSearch('')
  }, [buildingId, floorId])

  // @ts-expect-error Flatmap typing goes wrong here
  const data = (workspaces.data?.pages?.filter((page) => !!page)?.flatMap((page) => page?.data) || []) as Workspace[]
  if (workspace.data && !data.find((w) => w.id === workspace.data.id)) data.push(workspace.data)

  const items = data.length
    ? [
        {
          value: undefined,
          title: t('All workspaces'),
        },
        ...data
          // TODO This filtering should be removed in favor of adding buildingId to the workspace API
          .filter((workspace) => !buildingId || !floorIds?.length || floorIds.indexOf(workspace.floorId!) >= 0)
          // TODO Numeric sorting should be added to the workspace API
          .sort((a, b) => a.name?.localeCompare(b.name || '', 'en', { numeric: true, sensitivity: 'base' }) || 0)
          .map((workspace) => ({
            value: workspace.id,
            title: workspace.name,
          })),
      ]
    : []

  return (
    <Select
      placeholder={t('All workspaces')}
      value={value}
      style={styles.container}
      onSelect={onChange}
      onSearch={setSearch}
      searchable
      isSearching={!!search && workspaces.isFetching}
      isFetching={workspaces.isFetching}
      onEndReached={() => !workspaces.isFetchingNextPage && workspaces.fetchNextPage()}
      items={items}
      disabled={workspaces.isFetching}
      testID="WorkspaceSelect.Container"
      {...otherProps}
    />
  )
}

export default WorkspaceSelect
